/**
 * EmployeeLeaveAgentModal.jsx
 * ===========================
 * Chat modal for the Employee Leave Application agent (agent_key
 * "employee-leave-application"). Follows SupplierAgentModal: same layout, chat
 * bubbles, input bar, history control and next_field_ui widgets (local copies,
 * so this file is self-contained like the other agent modals) — plus
 * leave-specific date, review and result widgets.
 *
 * userMode, employee identity and the Oracle payload are decided server-side.
 * The browser only sends chat text and UI actions (select / edit / confirm).
 */

import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, User as UserIcon, Robot,
  CheckCircle, WarningCircle, PencilSimple, CalendarBlank, CircleNotch,
  PaperPlaneRight, Paperclip, Plus, CaretDown, Trash,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";

// ── Constants & helpers ──────────────────────────────────────────────────────

const LEAVE_AGENT_KEY = "employee-leave-application";
const LEAVE_API = "/erp-agents/employee-leave-application";
const LEAVE_SESSION_KEY = "leave_session_id";

const FIELD_LABELS = {
  employee: "Employee",
  employer: "Legal employer",
  absence_type: "Absence type",
  absence_reason: "Absence reason",
  start_date: "Start date",
  end_date: "End date",
  start_day_duration: "First day",
  end_day_duration: "Last day",
};

// Fields the user may reopen from the review / details panel.
const EDITABLE_FIELDS = [
  "employee", "employer", "absence_type", "absence_reason", "start_date", "end_date",
];

const TERMINAL_STAGES = ["submitted", "cancelled"];

function isTerminal(stage) {
  return TERMINAL_STAGES.includes(stage);
}

/** Request body for POST /chat. `message` is what the user sees in the bubble. */
function buildChatBody(sessionId, text, action) {
  const body = { session_id: sessionId, message: (text || "").trim() };
  if (action) body.action = action;
  return body;
}

function selectAction(field, value) {
  return { type: "select", field, value: String(value) };
}

function setValueAction(field, value) {
  return { type: "set_value", field, value: String(value) };
}

function editAction(field) {
  return { type: "edit", field };
}

function confirmAction(token) {
  return { type: "confirm", token };
}

/** Only the newest assistant message may render live widgets — older ones are
 *  history, so stale dropdowns / Submit buttons can't be clicked again. */
function latestAssistantId(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role === "assistant" && !m.loading) return m.id;
  }
  return null;
}

function durationLabel(v) {
  return Number(v) === 0.5 ? "Half day" : "Full day";
}

/** Rows for the review card / details panel, in a stable order. */
function reviewRows(fields) {
  const f = fields || {};
  const rows = [];
  for (const key of EDITABLE_FIELDS) {
    if (f[key] !== undefined && f[key] !== null && f[key] !== "") {
      rows.push({ key, label: FIELD_LABELS[key], value: String(f[key]), editable: true });
    }
  }
  if (f.start_day_duration !== undefined) {
    rows.push({ key: "start_day_duration", label: FIELD_LABELS.start_day_duration,
      value: durationLabel(f.start_day_duration), editable: false });
  }
  if (f.end_day_duration !== undefined && f.start_date !== f.end_date) {
    rows.push({ key: "end_day_duration", label: FIELD_LABELS.end_day_duration,
      value: durationLabel(f.end_day_duration), editable: false });
  }
  return rows;
}

/** Friendly text for an axios error, never exposing stack traces. */
function errorText(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 403) return "You don't have access to the Employee Leave Application agent.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (typeof detail === "string") return detail;
  if (!e?.response) return "Couldn't reach the server. Please check your connection and try again.";
  return "Something went wrong. Please try again.";
}

// ── Chat building blocks (same as SupplierAgentModal) ────────────────────────

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options = fieldUi.options || [];
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${fieldUi.field.replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-40 overflow-y-auto">
        {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No options available right now.</div>}
        {options.length > 0 && filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
        {filtered.slice(0, 50).map((opt) => (
          <div key={opt.value} onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RadioOptions({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-field-ui-${fieldUi.field}`}>
      {options.map((opt) => (
        <button key={opt.value} onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors">
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

function SupplierInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy,
  onAttach, attaching, attachedCount }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        {onAttach && (
          <>
            <input ref={fileInputRef} type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              multiple className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) onAttach(files);
                e.target.value = "";
              }}
              data-testid="agent-attach-input" />
            <button onClick={() => fileInputRef.current?.click()}
              disabled={attaching}
              title="Attach a file"
              data-testid="agent-attach-button"
              className="m-2 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#0020C2]
                hover:bg-gray-100 rounded transition-colors shrink-0 disabled:opacity-40 relative">
              {attaching
                ? <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                : <Paperclip size={16} weight="bold" />}
              {attachedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0020C2] text-white text-[9px] rounded-full flex items-center justify-center">
                  {attachedCount}
                </span>
              )}
            </button>
          </>
        )}
        <textarea ref={inputRef} value={value} onChange={onChange} onKeyDown={onKeyDown}
          rows={1} disabled={busy}
          placeholder="Continue the conversation…"
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }} />
        <button onClick={onSend} disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/erp-agents/${agentKey}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`);
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
        data-testid="chat-new" title="Start a new conversation">
        <Plus size={13} weight="bold" /> New Chat
      </button>
      <div className="relative">
        <button onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
          data-testid="chat-history-toggle">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
            data-testid="chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>
            )}
            {sessions.map((s) => (
              <div key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400">
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)}
                  className="text-[#DC2626] shrink-0 p-1">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function ProgressBar({ progress }) {
  const pct = progress?.match(/\((\d+)%\)/);
  if (!pct) return null;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct[1]}%` }} />
      </div>
    </div>
  );
}

// ── Leave-specific widgets ────────────────────────────────────────────────────

function DatePicker({ fieldUi, onPick, disabled }) {
  const [value, setValue] = React.useState("");
  return (
    <div className="mt-2 flex items-center gap-2" data-testid={`chat-field-ui-${fieldUi.field}`}>
      <CalendarBlank size={14} className="text-[#0020C2]" />
      <input type="date" value={value} min={fieldUi.min || undefined}
        onChange={(e) => setValue(e.target.value)} disabled={disabled}
        aria-label={fieldUi.field.replace(/_/g, " ")}
        className="text-xs border border-[#0020C2]/30 rounded px-2 py-1 outline-none focus:border-[#0020C2]" />
      <button onClick={() => value && onPick(value)} disabled={!value || disabled}
        className="text-xs font-bold px-3 py-1 bg-[#0020C2] text-white rounded disabled:bg-gray-200 disabled:text-gray-400">
        Use date
      </button>
    </div>
  );
}

function DurationSelect({ field, value, onChange, disabled }) {
  return (
    <select value={String(value ?? 1)} disabled={disabled}
      onChange={(e) => onChange(field, e.target.value)}
      data-testid={`leave-duration-${field}`}
      className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white">
      <option value="1">Full day</option>
      <option value="0.5">Half day</option>
    </select>
  );
}

function ReviewCard({ fieldUi, busy, onEdit, onDuration, onSubmit, onCancel }) {
  const rows = reviewRows(fieldUi.fields);
  const f = fieldUi.fields || {};
  const singleDay = f.start_date && f.start_date === f.end_date;
  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-lg overflow-hidden bg-white"
      data-testid="leave-review-card">
      <div className="px-3 py-2 text-xs font-bold text-[#0020C2] bg-[#0020C2]/5 border-b border-[#0020C2]/10">
        Review your leave request
      </div>
      <div className="divide-y divide-gray-50">
        {rows.filter((r) => r.editable).map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
            <span className="text-gray-500 shrink-0">{r.label}</span>
            <span className="text-gray-900 font-medium text-right truncate">{r.value}</span>
            <button onClick={() => onEdit(r.key)} disabled={busy}
              title={`Change ${r.label.toLowerCase()}`} data-testid={`leave-edit-${r.key}`}
              className="text-gray-400 hover:text-[#0020C2] disabled:opacity-40 shrink-0">
              <PencilSimple size={12} weight="bold" />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
          <span className="text-gray-500">{singleDay ? "Duration" : "First day"}</span>
          <DurationSelect field="start_day_duration" value={f.start_day_duration}
            onChange={onDuration} disabled={busy} />
        </div>
        {!singleDay && (
          <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
            <span className="text-gray-500">Last day</span>
            <DurationSelect field="end_day_duration" value={f.end_day_duration}
              onChange={onDuration} disabled={busy} />
          </div>
        )}
      </div>
      {fieldUi.notes?.length > 0 && (
        <ul className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 space-y-0.5 list-disc pl-6">
          {fieldUi.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
      <div className="flex border-t border-gray-100">
        <button onClick={onSubmit} disabled={busy} data-testid="leave-submit"
          className="flex-1 text-xs font-bold px-3 py-2 bg-[#0020C2] text-white hover:bg-[#001AA3] disabled:bg-gray-200 disabled:text-gray-400 flex items-center justify-center gap-1.5">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
          Submit to Oracle
        </button>
        <button onClick={onCancel} disabled={busy} data-testid="leave-cancel"
          className="text-xs font-bold px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 border-l border-gray-200 disabled:opacity-40">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ResultCard({ result }) {
  const ok = result?.leave_submitted;
  const ora = result?.oracle_response || {};
  if (!ok && !result?.oracle_error_kind) return null;
  return (
    <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${ok
      ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`}
      data-testid="leave-result">
      <div className="flex items-center gap-1.5 font-bold">
        {ok ? <CheckCircle size={13} weight="fill" /> : <WarningCircle size={13} weight="fill" />}
        {ok ? "Submitted to Oracle" : "Not submitted"}
      </div>
      {ok && (
        <div className="mt-1 space-y-0.5">
          <div>Absence ID: <strong>{ora.personAbsenceEntryId}</strong></div>
          {(ora.absenceStatusCd || ora.absenceDispStatus) && (
            <div>Status: <strong>{ora.absenceStatusCd || ora.absenceDispStatus}</strong></div>
          )}
          {ora.approvalStatusCd && <div>Approval: <strong>{ora.approvalStatusCd}</strong></div>}
        </div>
      )}
    </div>
  );
}

function LeaveChatBubble({ msg, live, busy, handlers }) {
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading && live ? msg.result?.next_field_ui : null;
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
        ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser ? "bg-[#0020C2] text-white rounded-tr-sm"
          : msg.error ? "bg-red-50 border border-red-200 text-red-700 rounded-tl-sm"
          : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5" aria-label="Working">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}
        {!msg.loading && msg.text && (
          isUser ? <p className="whitespace-pre-wrap">{msg.text}</p> : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}
        {fieldUi?.error && <div className="mt-2 text-xs text-red-600">{fieldUi.error}</div>}
        {fieldUi?.uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(o) => handlers.select(fieldUi.field, o)} />
        )}
        {fieldUi?.uiType === "radio" && (
          <RadioOptions fieldUi={fieldUi} onPick={(o) => handlers.select(fieldUi.field, o)} />
        )}
        {fieldUi?.uiType === "date" && (
          <DatePicker fieldUi={fieldUi} disabled={busy}
            onPick={(d) => handlers.setValue(fieldUi.field, d, d)} />
        )}
        {fieldUi?.uiType === "review" && (
          <ReviewCard fieldUi={fieldUi} busy={busy} onEdit={handlers.edit}
            onDuration={(f, v) => handlers.setValue(f, v, `${f.startsWith("start") ? "First" : "Last"} day: ${durationLabel(v)}`)}
            onSubmit={() => handlers.confirm(fieldUi.token)} onCancel={handlers.cancel} />
        )}
        {!isUser && !msg.loading && <ResultCard result={msg.result} />}
        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>{msg.timestamp}</div>
        )}
      </div>
    </div>
  );
}

function DetailsPanel({ result, busy, onEdit }) {
  const fields = result?.fields_collected || {};
  const rows = reviewRows(fields);
  const missing = result?.fields_missing || [];
  const locked = isTerminal(result?.stage) || result?.stage === "submitting";
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="leave-details-panel">
      <div className="text-sm font-bold text-gray-800">Request details</div>
      {rows.length === 0 && <p className="text-xs text-gray-400">Details appear here as you chat.</p>}
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-2 text-xs border-b border-gray-50 py-1.5">
            <span className="text-gray-500">{r.label}</span>
            <span className="flex items-center gap-2 text-gray-900 font-medium text-right">
              {r.value}
              {r.editable && !locked && (
                <button onClick={() => onEdit(r.key)} disabled={busy} title={`Change ${r.label.toLowerCase()}`}
                  className="text-gray-400 hover:text-[#0020C2] disabled:opacity-40">
                  <PencilSimple size={11} weight="bold" />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
      {missing.length > 0 && !locked && (
        <div className="text-xs text-gray-500">Still needed: {missing.join(", ")}</div>
      )}
      {result?.stage && (
        <div className="text-[11px] text-gray-400">Status: {String(result.stage).replace(/_/g, " ")}</div>
      )}
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────

export default function EmployeeLeaveAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();
  const agent = agents.find((a) => a.key === openAgent);

  const [sessionId, setSessionId] = React.useState(() => {
    const stored = sessionStorage.getItem(LEAVE_SESSION_KEY);
    if (stored) return stored;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(LEAVE_SESSION_KEY, fresh);
    return fresh;
  });
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [last, setLast] = React.useState(null);
  const busyRef = React.useRef(false);          // guards double clicks before re-render
  const startedFor = React.useRef(null);
  const endRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const pushAssistant = (data, now) => setMessages((prev) => prev.map((m) => (m.loading
    ? { ...m, loading: false, text: data.reply || "", result: data, timestamp: now } : m)));

  // Lazy /start per session (also resumes an existing Redis session)
  React.useEffect(() => {
    if (openAgent !== LEAVE_AGENT_KEY || startedFor.current === sessionId) return;
    startedFor.current = sessionId;
    // Append (not replace) so history loaded by openSession stays visible.
    setMessages((prev) => [...prev, { id: Date.now(), role: "assistant", loading: true }]);
    busyRef.current = true;
    setBusy(true);
    api.post(`${LEAVE_API}/start`, { session_id: sessionId })
      .then(({ data }) => {
        setLast(data);
        setMessages((prev) => {
          const rest = prev.filter((m) => !m.loading);
          const tail = rest[rest.length - 1];
          // Resumed session: attach live widgets to the last reply instead of repeating it.
          if (tail && tail.role === "assistant" && tail.text === data.reply) {
            return [...rest.slice(0, -1), { ...tail, result: data }];
          }
          return [...rest, { id: Date.now(), role: "assistant", text: data.reply || "", result: data, timestamp: ts() }];
        });
      })
      .catch((e) => setMessages((prev) => prev.map((m) => (m.loading
        ? { ...m, loading: false, error: true, text: errorText(e), timestamp: ts() } : m))))
      .finally(() => { busyRef.current = false; setBusy(false); });
  }, [openAgent, sessionId]);

  const send = async (displayText, action) => {
    const text = (displayText || "").trim();
    if ((!text && !action) || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    setMessages((prev) => [
      ...prev,
      ...(text ? [{ id: Date.now(), role: "user", text, timestamp: ts() }] : []),
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    try {
      const { data } = await api.post(`${LEAVE_API}/chat`, buildChatBody(sessionId, text, action));
      setLast(data);
      pushAssistant(data, ts());
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.loading
        ? { ...m, loading: false, error: true, text: errorText(e), timestamp: ts() } : m)));
    } finally {
      busyRef.current = false;
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handlers = {
    select: (field, opt) => send(opt.label, selectAction(field, opt.value)),
    setValue: (field, value, label) => send(label || String(value), setValueAction(field, value)),
    edit: (field) => send(`Change the ${field.replace(/_/g, " ")}`, editAction(field)),
    confirm: (token) => send("Submit", confirmAction(token)),
    cancel: () => send("Cancel this request", { type: "cancel" }),
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };
  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(`/erp-agents/${LEAVE_AGENT_KEY}/sessions/new`);
      if (data?.session_id) newId = data.session_id;
    } catch { /* local uuid is fine */ }
    sessionStorage.setItem(LEAVE_SESSION_KEY, newId);
    setMessages([]);
    setLast(null);
    setInput("");
    setSessionId(newId);
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/${LEAVE_AGENT_KEY}/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i, role: m.role, text: m.content,
        timestamp: m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
      }));
      sessionStorage.setItem(LEAVE_SESSION_KEY, sid);
      startedFor.current = null;
      setSessionId(sid);          // /start resumes Redis state and returns live widgets
      setMessages(loaded);
    } catch (e) {
      window.alert(`Could not open conversation: ${errorText(e)}`);
    }
  };

  const close = () => { setOpenAgent(null); onClose?.(); };
  const liveId = latestAssistantId(messages);

  if (openAgent !== LEAVE_AGENT_KEY) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} data-testid={`agent-modal-${openAgent}`}
        className="bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden"
        style={{ width: "min(92vw,1200px)", height: "90vh" }}>

        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion HCM · Vaku AI</div>
            <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label || "Employee Leave Application"}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <ChatHistoryControl agentKey={LEAVE_AGENT_KEY} currentSessionId={sessionId}
              onNewChat={startNewChat} onOpenSession={openSession} />
            <div className="w-px h-5 bg-gray-200" />
            <button onClick={() => { close(); setSetupOpen(true); }}
              className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
              <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
            </button>
            <button onClick={close} data-testid="agent-modal-close" aria-label="Close"><X size={18} /></button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-col overflow-hidden border-r border-gray-200" style={{ width: "62%" }}>
            <ProgressBar progress={last?.progress} />
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50" aria-live="polite">
              {messages.map((m) => (
                <LeaveChatBubble key={m.id} msg={m} live={m.id === liveId} busy={busy} handlers={handlers} />
              ))}
              <div ref={endRef} />
            </div>
            <SupplierInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
              onKeyDown={handleKeyDown} onSend={() => send(input)} busy={busy} />
          </div>
          <div className="flex flex-col bg-white" style={{ width: "38%", minWidth: "300px" }}>
            <DetailsPanel result={last} busy={busy} onEdit={handlers.edit} />
          </div>
        </div>
      </div>
    </div>
  );
}